import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';
import { BTN } from '../sim/input';
import { passChargeFor } from '../sim/passing';
import { predictKickTrajectory } from '../sim/trajectory';
import { laneOpen } from './actPlayer';

// D46 Items 1+2+5 — the ONE shared keeper-distribution brain (pure). Every keeper release site (GK_HOLD
// distribute, goal-kick take, feet clear) routes its aim through pickDistribution so a keeper can NEVER
// kick own-goal-ward and never gift a flat ball across a camped opponent:
//   • candidate filter — a target must be UPFIELD (hard rule), on a clean lane, and ≥ DIST_MIN_M away.
//   • safety cone — the final aim's forward component must exceed DIST_MIN_UPFIELD; the aim is clamped to
//     the cone edge if the target/soft-lock math would violate it (the own-goal killer).
//   • no safe flat target ⇒ a LOFTED PUNT to the emptier deep flank — a high ball a camped press cannot
//     flat-intercept. This is also the standoff-deadlock escape (a punt always qualifies).

export interface Distribution {
  aim: number; // world-space yaw, already clamped into the safety cone
  button: number; // BTN.PASS (flat) or BTN.SHOOT (lofted punt)
  chargeTarget: number; // charge ticks to hold before release
  aimElev: number; // 0 for a flat pass; PUNT_ELEV for a lofted punt
  lofted: boolean;
}

const angleTo = (from: Vec2, to: Vec2): number => Math.atan2(to.y - from.y, to.x - from.x);
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Clamp `aim` so its forward (attack-direction) component exceeds `minFwd` — the own-goal killer. Keeps
 *  the lateral (left/right) sense; only pulls a too-square or backward aim forward to the cone edge. */
export function clampToCone(aim: number, dir: number, minFwd: number): number {
  const forward = dir > 0 ? 0 : Math.PI; // world yaw of "upfield"
  let rel = aim - forward;
  rel = Math.atan2(Math.sin(rel), Math.cos(rel)); // normalize to (−π, π]
  const edge = Math.acos(Math.max(-1, Math.min(1, minFwd))) - 1e-3; // |rel| ≤ edge ⇒ cos(rel) > minFwd
  if (rel > edge) rel = edge;
  else if (rel < -edge) rel = -edge;
  return forward + rel;
}

/** Distance from `t` to its nearest opponent (how "open" the man is). */
function nearestOpp(state: MatchState, gk: PlayerState, t: PlayerState): number {
  let best = Infinity;
  for (const o of state.players) {
    if (o.team === gk.team || o.sentOff) continue;
    best = Math.min(best, dist(t.pos, o.pos));
  }
  return best;
}

/**
 * A legal distribution target: our man, UPFIELD of the keeper, on a clean lane, ≥ DIST_MIN_M away — and
 * D133: **not marked tighter than `DIST_OPEN_MIN`**. That last one is the whole of issue 6. `qualifies`
 * checked lane + upfield + distance and never whether the receiver himself was covered, so the keeper
 * would feed a man with a defender at his shoulder and call it a legal option.
 */
function qualifies(state: MatchState, gk: PlayerState, t: PlayerState, dir: number, cfg: SimConfig): boolean {
  if (t.team !== gk.team || t.id === gk.id || t.sentOff) return false;
  if (dir * (t.pos.x - gk.pos.x) <= 0) return false; // (a) upfield — hard rule, no exceptions
  if (dist(gk.pos, t.pos) < cfg.actions.gk.DIST_MIN_M) return false; // (c) far enough to be a real pass
  if (nearestOpp(state, gk, t) < cfg.actions.gk.DIST_OPEN_MIN) return false; // (d) D133: he must be OPEN
  if (!laneOpen(state, gk.pos, t.pos, gk.team, cfg)) return false; // (b) reachable — not "widest-open behind a wall"
  return true;
}

/**
 * D133 (§4, PT4-P8) — the target SCORE. This used to be an absolute priority chain (a calling human, then
 * the nearest human, then the widest-open man), which had two failure modes the clip shows: a MARKED
 * caller forced a doomed feed, and a single qualifier was taken however tightly covered.
 *
 * Now: `W_OPEN·openness + W_CLEAR·laneClearance + W_PROG·progress − W_NEAR·distance` (the last only for
 * a goal-kick lay-off, D55), with the human and caller preferences as **bonuses**. A viable caller still
 * wins comfortably; a covered one loses to an open man, which is what a keeper with eyes would do.
 *
 * Deterministic and rng-free: every term is geometry, and exact ties keep the lower EntityId.
 */
function targetScore(state: MatchState, gk: PlayerState, t: PlayerState, humans: ReadonlySet<EntityId>, cfg: SimConfig, prefer: 'open' | 'nearest', avoid: EntityId | null): number {
  const g = cfg.actions.gk;
  const pitch = PITCH[state.mode.teamSize];
  const dir = state.attackDir[gk.team];
  const d = dist(gk.pos, t.pos);
  const openness = Math.min(nearestOpp(state, gk, t), 12) / 12;
  const clear = Math.min(laneGap(state, gk.pos, t.pos, gk.team), 6) / 6;
  const progress = Math.max(0, Math.min(1, (dir * (t.pos.x - gk.pos.x)) / (pitch.length / 2)));
  let score = g.DIST_W_OPEN * openness + g.DIST_W_CLEAR * clear + g.DIST_W_PROG * progress;
  if (prefer === 'nearest') score -= g.DIST_W_NEAR * Math.min(1, d / (pitch.length / 2));
  if (humans.has(t.id)) score += g.DIST_HUMAN_BONUS;
  if (humans.has(t.id) && t.calling) score += g.DIST_CALL_BONUS;
  // D59: the mate whose back-pass I am holding is the LAST resort, not the first reflex — a back-pass
  // means "switch the play". Still a penalty rather than an exclusion, so he can win when he is the
  // only sane ball.
  if (avoid !== null && t.id === avoid) score -= g.DIST_AVOID_PENALTY;
  return score;
}

/** The narrowest gap any opponent leaves beside the from→to lane (∞ when nobody is in it). */
function laneGap(state: MatchState, from: Vec2, to: Vec2, team: number): number {
  const gx = to.x - from.x;
  const gy = to.y - from.y;
  const len = Math.hypot(gx, gy);
  if (len < 1e-6) return Infinity;
  const ux = gx / len;
  const uy = gy / len;
  let worst = Infinity;
  for (const o of state.players) {
    if (o.team === team || o.sentOff) continue;
    const rx = o.pos.x - from.x;
    const ry = o.pos.y - from.y;
    const along = rx * ux + ry * uy;
    if (along <= 0 || along >= len) continue;
    worst = Math.min(worst, Math.abs(rx * uy - ry * ux));
  }
  return worst;
}

function pickTarget(
  state: MatchState,
  gk: PlayerState,
  humans: ReadonlySet<EntityId>,
  dir: number,
  cfg: SimConfig,
  prefer: 'open' | 'nearest' = 'open',
  avoid: EntityId | null = null,
): PlayerState | null {
  let best: PlayerState | null = null;
  let bestScore = -Infinity;
  for (const t of state.players) {
    // ascending EntityId, strict > ⇒ exact ties keep the lower id (the house tie-break)
    if (!qualifies(state, gk, t, dir, cfg)) continue;
    const sc = targetScore(state, gk, t, humans, cfg, prefer, avoid);
    if (sc > bestScore) {
      bestScore = sc;
      best = t;
    }
  }
  return best;
}

/** D84: the lofted-punt TARGET — roughly the MIDDLE OF THE OPPOSITE HALF (x = dir·length/4, the user's
 *  "land in the middle of the opposite half"), on the emptier flank (fewest opponents within 12 m). D55's
 *  halfway-line target undershot; the clamp in pickDistribution keeps an overhit off the far wall. */
export function puntTarget(state: MatchState, gk: PlayerState, dir: number, cfg: SimConfig): Vec2 {
  const g = cfg.actions.gk;
  const pitch = PITCH[state.mode.teamSize];
  const depthX = dir * pitch.length * g.PUNT_DEPTH_FRAC; // middle of the OPPOSITE half
  const wideY = pitch.width * g.PUNT_FLANK_FRAC;
  const flanks: Vec2[] = [
    { x: depthX, y: wideY },
    { x: depthX, y: -wideY },
  ];
  // D133: the flank score SEES SHIRTS. It counted opponents only, so "the empty flank" was empty of our
  // players too — a punt into nobody, which is how a clearance becomes the other team's possession.
  // Score = (ours − theirs) within PUNT_SCAN_R; ties break toward our nearest man, then toward +y so the
  // choice is total and deterministic. The three magic literals it used to carry are config now (§17).
  let best = flanks[0]!;
  let bestScore = -Infinity;
  let bestOurs = Infinity;
  for (const f of flanks) {
    let n = 0;
    let ourNearest = Infinity;
    for (const o of state.players) {
      if (o.sentOff) continue;
      const d = dist(o.pos, f);
      if (o.team === gk.team) {
        if (o.id !== gk.id) ourNearest = Math.min(ourNearest, d);
        if (d < g.PUNT_SCAN_R && o.id !== gk.id) n += 1;
      } else if (d < g.PUNT_SCAN_R) n -= 1;
    }
    if (n > bestScore || (n === bestScore && ourNearest < bestOurs)) {
      bestScore = n;
      bestOurs = ourNearest;
      best = f;
    }
  }
  return best;
}

/** D55: LEAST charge whose lofted punt carries ≈ `distM` before first touchdown — bisected against the
 *  real flight model (predictKickTrajectory mirrors applyKicks + the §5.3 ball). The old
 *  passChargeFor(flat-roll) wildly overshot for a lofted ball, and D49 auto-power then maxed it — the
 *  "Abstoß lands in the opponent box" rocket. */
function puntChargeFor(distM: number, elev01: number, cfg: SimConfig, conditions: MatchState['conditions']): number {
  const carry = (charge: number): number => {
    const pts = predictKickTrajectory(cfg, { x: 0, y: 0, z: 0.11 }, 0, elev01, BTN.SHOOT, charge, conditions, 90);
    for (let i = 1; i < pts.length; i++) {
      if (pts[i]!.z <= 0.01 && pts[i - 1]!.z > 0.01) return pts[i]!.x; // first touchdown
    }
    return pts[pts.length - 1]!.x;
  };
  if (carry(cfg.actions.kick.CHARGE_CAP) <= distM) return cfg.actions.kick.CHARGE_CAP;
  let lo = 1;
  let hi = cfg.actions.kick.CHARGE_CAP;
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) / 2;
    if (carry(mid) >= distM) hi = mid;
    else lo = mid;
  }
  return Math.max(2, Math.ceil(hi));
}

/** The keeper's safe distribution this tick. Always returns a legal (cone-satisfying) release.
 *  D55 `prefer`: 'nearest' = a restart lay-off to the next available man (goal kicks / post-goal). */
export function pickDistribution(
  state: MatchState,
  gk: PlayerState,
  humans: ReadonlySet<EntityId>,
  cfg: SimConfig,
  prefer: 'open' | 'nearest' = 'open',
  avoid: EntityId | null = null, // D59: the mate who back-passed to me this spell
): Distribution {
  const dir = state.attackDir[gk.team];
  const minFwd = cfg.actions.gk.DIST_MIN_UPFIELD;
  const target = pickTarget(state, gk, humans, dir, cfg, prefer, avoid);
  if (target) {
    // D133: LEAD the man. A keeper's flat release carries `assist:false` (D46) so it skips the D34
    // soft-lock's own lead entirely, and it is calibrated to still be travelling ~9 m/s when it gets
    // there — so an imperfect reception rolls 10–20 m on into the counter-press (the clip: tick 2569,
    // ~19 m past receiver 4, trapped by opponent 9). Aim where he WILL be, capped, and cone-clamp AFTER
    // the lead is computed — the D62 lesson: clamping first and leading second re-opens the own goal.
    const lead = Math.min(cfg.actions.gk.DIST_LEAD_CAP_SEC, dist(gk.pos, target.pos) / 20);
    const at = { x: target.pos.x + target.vel.x * lead, y: target.pos.y + target.vel.y * lead };
    const aim = clampToCone(angleTo(gk.pos, at), dir, minFwd);
    return { aim, button: BTN.PASS, chargeTarget: passChargeFor(dist(gk.pos, target.pos), cfg, state.conditions), aimElev: 0, lofted: false };
  }
  // no safe flat lane ⇒ a lofted punt over the press, dropping in the MIDDLE OF THE OPPOSITE HALF (D84)
  // on the emptier flank. SHOOT skips pass-assist by design; the loft makes it un-flat-interceptable.
  const pitch = PITCH[state.mode.teamSize];
  const tgt = puntTarget(state, gk, dir, cfg);
  const aim = clampToCone(angleTo(gk.pos, tgt), dir, minFwd);
  // D84: clamp the carry so first touchdown (+ its bounce) stays short of the far wall — the "overhit that
  // bounces back to the keeper" on walled sizes was the far wall returning a rocket. Carry to the target,
  // but never past (distance to the far wall − BOUNCE_MARGIN).
  const wallX = (dir * pitch.length) / 2;
  const distToWall = Math.abs(wallX - gk.pos.x);
  const landing = Math.min(dist(gk.pos, tgt), distToWall - cfg.actions.gk.PUNT_BOUNCE_MARGIN);
  const chargeTarget = puntChargeFor(landing, cfg.actions.gk.PUNT_ELEV, cfg, state.conditions);
  return { aim, button: BTN.SHOOT, chargeTarget, aimElev: cfg.actions.gk.PUNT_ELEV, lofted: true };
}
